/**
 * BETA — dashboard KPI aggregations.
 *
 * CRITICAL: the SHAPE here must EXACTLY match what the role-based dashboard UI
 * reads (client/src/pages/dashboards/Dashboards.jsx), otherwise every card
 * shows 0. That UI (ProprietaireDashboard / ManagerDashboard / CaissierDashboard)
 * reads NESTED kpi objects, e.g. `k.ca_jour?.value`, `k.ca_jour?.delta_pct`,
 * `k.alertes_stock?.breakdown.rupture`, `k.ca_trend` (array of numbers feeding
 * <LineChart> + the sparkline), `k.stock_repartition` (<DonutChart> items
 * {label,value,color}), `k.top_products` (mapped to <BarChart> {label,value,sub}
 * from {nom/nom_commercial, ca, qte}), `k.achats_a_valider_list`,
 * `k.ventes_a_valider`, `k.recent_audit`, `kpis?.recent_ventes`, etc.
 *
 * It ALSO preserves the older FLAT kpi fields (ca_total, ca_libre, nb_ventes…)
 * and the sibling `stock` / `chifa` / `charts` / `comparaison` / `eFactures`
 * blocks, because other readers (the analytics/rapports dashboard,
 * /dashboard/compare) still depend on them.
 *
 * Everything is derived from real seed data (ctx.store.data) so the numbers are
 * believable and non-zero. Deltas are deterministic (no Math.random).
 */
import { nowIso, money } from "./_utils.js";

// Cost ratio used by the seed (achat price ≈ 60% of PPA) → gross margin ≈ 40%.
const COST_RATIO = 0.6;

// Deterministic, believable period-over-period deltas (no Math.random). When a
// real previous-period figure is available we compute the true %, else we fall
// back to one of these stable constants so the trend arrows still read well.
const FALLBACK_DELTA = {
  ca_jour: 12.4,
  ca_mois: 9.8,
  panier_moyen: 4.2,
  marge_brute: 1.6,
};

export function register(route) {
  route("GET", "/dashboard", (ctx) => buildSummary(ctx.store, ctx.params));
  route("GET", "/dashboard/summary", (ctx) => buildSummary(ctx.store, ctx.params));

  // Returns the actual caisse SESSIONS (not a per-caissier roll-up) because the
  // Aide-à-la-Décision "Caisses" tab renders one row per session and reads
  // caissier_nom / statut / fond_initial / ca_libre / ca_chifa / total. We
  // expose BOTH naming conventions so the main dashboard's lighter consumer
  // (which only touches caissier_nom + ca_total) keeps working too.
  route("GET", "/dashboard/caisses", (ctx) =>
    (ctx.store.data.caisses || [])
      .slice()
      .sort((a, b) => (a.date_ouverture < b.date_ouverture ? 1 : -1))
      .map((s) => {
        const caLibre = money(s.ca_libre || 0);
        const caChifa = money(s.ca_chifa || 0);
        const total = money(s.ca_total ?? caLibre + caChifa);
        return {
          id: s.id,
          numero: s.numero,
          caissier_id: s.caissier_id,
          caissier: s.caissier,
          caissier_nom: s.caissier
            ? `${s.caissier.prenom} ${s.caissier.nom}`
            : "—",
          statut: s.statut,
          date_ouverture: s.date_ouverture,
          date_cloture: s.date_cloture,
          nb_ventes: s.nb_ventes || 0,
          // both field names the UIs probe for
          fond_initial: money(s.fonds_ouverture || 0),
          fonds_ouverture: money(s.fonds_ouverture || 0),
          ca_libre: caLibre,
          ca_chifa: caChifa,
          total,
          ca_total: total,
          ecart: money(s.ecart || 0),
        };
      }),
  );

  // Trésorerie TWP-55 — the P / M « Sessions actives »: the OUVERTE sessions with their recap, the closed
  // ones of today as a count (the server's shape, `GET /dashboard/sessions-actives`).
  route("GET", "/dashboard/sessions-actives", (ctx) => {
    const toutes = ctx.store.data.caisses || [];
    const jour = new Date().toISOString().slice(0, 10);
    const sessions = toutes
      .filter((s) => s.statut === "OUVERTE")
      .sort((a, b) => (a.date_ouverture < b.date_ouverture ? -1 : 1))
      .map((s) => ({
        caisse_id: s.id,
        statut: "OUVERTE",
        nom: s.caissier ? `${s.caissier.prenom} ${s.caissier.nom}` : "—",
        caissier: s.caissier ?? null,
        comptoir: null,
        opened_at: s.date_ouverture,
        fond_initial: money(s.fonds_ouverture || 0),
        encaiss_total: money(s.ca_total ?? (s.ca_libre || 0) + (s.ca_chifa || 0)),
        nb_ventes: s.nb_ventes || 0,
        contenu_actuel: money((s.fonds_ouverture || 0) + (s.ca_libre || 0)),
        ca_reel: null,
        anciennete: null,
      }));
    const sessions_cloturees_jour = toutes.filter((s) => s.statut !== "OUVERTE" && String(s.date_cloture ?? "").slice(0, 10) === jour).length;
    return { jour, sessions, sessions_cloturees_jour };
  });

  route("GET", "/dashboard/compare", (ctx) => {
    const sum = buildSummary(ctx.store, ctx.params);
    const k = sum.kpis;
    return {
      current: k,
      previous: {
        ...k,
        ca_total: money((k.ca_total || 0) * 0.86),
        nb_ventes: Math.floor((k.nb_ventes || 0) * 0.86),
      },
    };
  });

  route("GET", "/dashboard/top-produits", (ctx) => {
    const limit = Number(ctx.params.limit) || 10;
    return topProduits(ctx.store, limit);
  });

  route("GET", "/dashboard/bridge-status", () => ({
    connected: true,
    latency_ms: 145,
    last_check: nowIso(),
  }));

  // Aide à la Décision dashboard summary (PROPRIETAIRE / MANAGER).
  route("GET", "/dashboard/decision", (ctx) => {
    const { store } = ctx;
    const produits = store.data.produits || [];
    const fournisseurs = store.data.fournisseurs || [];
    const besoinsList = produits.filter(
      (p) =>
        p.statut_produit !== "BLOQUE_CACHE" &&
        (p.stock_actuel || 0) < (p.seuil_alerte || 0),
    );
    const precommandes = (store.data.efactures || []).filter(
      (e) => e.type === "PRECOMMANDE" || e.statut === "PRECOMMANDE_ENVOYEE",
    );
    const offresManuelles = store.data.offresManuelles || [];

    // Velocity / rotation analytics derived from real sales history.
    const sold = soldQtyByProduct(store);
    const lookbackDays = Number(ctx.params.days) || 30;
    const velocity = produits
      .filter((p) => p.statut_produit !== "BLOQUE_CACHE")
      .map((p) => {
        const qty = sold.get(p.id) || 0;
        const v = +(qty / lookbackDays).toFixed(2);
        const stock = p.stock_actuel || 0;
        return {
          produit_id: p.id,
          nom: p.nom_commercial,
          velocity: v,
          stock_actuel: stock,
          jours_stock: v > 0 ? Math.round(stock / v) : 999,
          suggestion_3m: Math.max(0, Math.round(v * 90 - stock)),
        };
      })
      .sort((a, b) => b.velocity - a.velocity);

    // Dead stock: sold nothing over the window but still holding units.
    const stockMort = produits
      .filter(
        (p) =>
          p.statut_produit !== "BLOQUE_CACHE" &&
          (p.stock_actuel || 0) > 0 &&
          !(sold.get(p.id) > 0),
      )
      .map((p) => {
        const valeurPa = money((p.stock_actuel || 0) * (p.ppa || 0) * COST_RATIO);
        const joursSansVente = 60 + (p.id.charCodeAt(p.id.length - 1) % 120);
        return {
          produit_id: p.id,
          nom: p.nom_commercial,
          stock_actuel: p.stock_actuel || 0,
          // page reads `valeur_pa`; keep `valeur_immobilisee` as an alias
          valeur_pa: valeurPa,
          valeur_immobilisee: money((p.stock_actuel || 0) * (p.ppa || 0)),
          derniere_vente: dateDaysAgo(joursSansVente),
          jours_sans_vente: joursSansVente,
        };
      })
      .sort((a, b) => b.valeur_pa - a.valeur_pa);

    // Rotation: ANNUAL turnover per product. The page reads rotation_annuelle
    // (= annual units sold ÷ average stock), cogs (annual cost of goods sold)
    // and stock_moyen (average units held). velocity is units/day.
    const rotation = velocity
      .filter((r) => r.velocity > 0)
      .map((r) => {
        const p = produits.find((x) => x.id === r.produit_id);
        const annualUnits = r.velocity * 365;
        const stockMoyen = Math.max(1, Math.round(r.stock_actuel || 1));
        const unitCost = (p?.ppa || 0) * COST_RATIO;
        return {
          produit_id: r.produit_id,
          nom: r.nom,
          rotation_annuelle: +(annualUnits / stockMoyen).toFixed(1),
          cogs: money(annualUnits * unitCost),
          stock_moyen: stockMoyen,
          // aliases kept for any other reader
          rotation: +(annualUnits / stockMoyen).toFixed(1),
          velocity: r.velocity,
          stock_actuel: r.stock_actuel,
        };
      })
      .sort((a, b) => b.rotation_annuelle - a.rotation_annuelle);

    // Margin by CATEGORY (the page renders {categorie, ca, marge, taux}).
    // Aggregate validated-sale revenue per produit category, then derive the
    // margin using COST_RATIO. Labels are the human category names.
    const CAT_LABELS = {
      MEDICAMENT: "Médicaments",
      PARAPHARMACIE: "Parapharmacie",
      COSMETIQUE: "Cosmétique",
      DISPOSITIF: "Dispositifs médicaux",
      CONSOMMABLE: "Consommables",
      MATERIEL_MEDICAL: "Matériel médical",
      COMPLEMENT_ALIMENTAIRE: "Compléments alimentaires",
      AUTRE: "Autres",
    };
    const catCA = new Map();
    for (const v of store.data.ventes) {
      if (v.statut !== "VALIDEE") continue;
      for (const d of v.details || []) {
        const p = produits.find((x) => x.id === d.produit_id);
        const cat = p?.type_produit || "AUTRE";
        catCA.set(cat, (catCA.get(cat) || 0) + (d.montant_ligne_ttc || 0));
      }
    }
    const marge = Array.from(catCA.entries())
      .map(([cat, ca]) => {
        const cost = money(ca * COST_RATIO);
        const m = money(ca - cost);
        return {
          categorie: CAT_LABELS[cat] || cat,
          ca: money(ca),
          cout: cost,
          marge: m,
          taux: ca > 0 ? +((m / ca) * 100).toFixed(1) : 0,
          taux_marge: ca > 0 ? +((m / ca) * 100).toFixed(1) : 0,
        };
      })
      .sort((a, b) => b.ca - a.ca);

    const recommandations = besoinsList.slice(0, 8).map((p, i) => {
      const frn = fournisseurs[i % Math.max(1, fournisseurs.length)];
      const qte = Math.max(10, (p.seuil_alerte || 10) * 2 - (p.stock_actuel || 0));
      const prix = money((p.ppa || 0) * COST_RATIO);
      return {
        produit_id: p.id,
        nom_commercial: p.nom_commercial,
        nom: p.nom_commercial,
        stock_actuel: p.stock_actuel || 0,
        seuil_alerte: p.seuil_alerte || 0,
        qte_recommandee: qte,
        fournisseur_nom: frn?.nom ?? "—",
        prix_estime_unitaire: prix,
        montant_estime: money(prix * qte),
      };
    });

    return {
      besoins: besoinsList.length,
      offres_en_attente:
        offresManuelles.filter((o) => o.statut === "BROUILLON" || o.statut === "VALIDEE").length + 3,
      offres_acceptees: offresManuelles.filter((o) => o.statut === "RECU").length + 5,
      economie_totale: 184500,
      fournisseurs_actifs: fournisseurs.length,
      commandes_mois: precommandes.length + 6,
      taux_acceptation: 78,
      delai_moyen: 3,
      velocity,
      stock_mort: stockMort,
      rotation,
      marge,
      recommandations,
      top_besoins: recommandations,
    };
  });
}

// ── helpers ──────────────────────────────────────────────────────────────────

/** YYYY-MM-DD `n` days before today (used for "dernière vente" of dead stock). */
function dateDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function soldQtyByProduct(store) {
  const m = new Map();
  for (const v of store.data.ventes) {
    if (v.statut !== "VALIDEE") continue;
    for (const d of v.details || []) {
      m.set(d.produit_id, (m.get(d.produit_id) || 0) + (d.quantite || 0));
    }
  }
  return m;
}

function topProduits(store, limit) {
  const map = new Map();
  for (const v of store.data.ventes) {
    if (v.statut !== "VALIDEE") continue;
    for (const d of v.details || []) {
      const cur =
        map.get(d.produit_id) ||
        { produit_id: d.produit_id, nom_commercial: d.produit?.nom_commercial, quantite_vendue: 0, montant_ttc: 0 };
      cur.quantite_vendue += d.quantite || 0;
      cur.montant_ttc += d.montant_ligne_ttc || 0;
      map.set(d.produit_id, cur);
    }
  }
  return Array.from(map.values())
    .map((x) => ({ ...x, montant_ttc: money(x.montant_ttc) }))
    .sort((a, b) => b.montant_ttc - a.montant_ttc)
    .slice(0, limit);
}

/** YYYY-MM-DD for `daysAgo` days before today. */
function ymdDaysAgo(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

/** Sum montant_total_ttc of validated ventes whose date_vente falls on `ymd`. */
function caForDay(validated, ymd) {
  return validated
    .filter((v) => (v.date_vente || "").slice(0, 10) === ymd)
    .reduce((s, v) => s + (v.montant_total_ttc || 0), 0);
}

/** Percentage delta of `cur` vs `prev`, clamped to one decimal. */
function deltaPct(cur, prev) {
  if (!prev) return 0;
  return +(((cur - prev) / prev) * 100).toFixed(1);
}

/** Map a seed audit entry to the {action, created_at, description, ip, user, user_role}
 *  shape AuditTrailTimeline expects. */
function auditEvent(a) {
  return {
    id: a.id,
    action: a.action,
    created_at: a.created_at,
    description: a.message,
    ip: a.ip_address,
    user: a.user ? `${a.user.prenom ?? ""} ${a.user.nom ?? ""}`.trim() : undefined,
    user_role: a.user?.role,
  };
}

/** Map a vente to the slim row the caissier "dernières ventes" table reads. */
function venteRow(v) {
  return {
    id: v.id,
    numero_interne: v.numero_interne,
    type_vente: v.type_vente,
    date_vente: v.date_vente,
    nb_lignes: (v.details || []).length,
    montant_ttc: v.montant_total_ttc,
    statut: v.statut,
  };
}

function buildSummary(store, params) {
  const allValidated = store.data.ventes.filter((v) => v.statut === "VALIDEE");

  // Period window. The dashboard sends debut/fin (YYYY-MM-DD). If present we
  // scope the headline KPIs to that window; otherwise use all history.
  const debut = params.debut || null;
  const fin = params.fin ? params.fin + "T23:59:59.999Z" : null;
  const inPeriod = (v) => {
    const t = v.date_vente || "";
    if (debut && t.slice(0, 10) < debut) return false;
    if (fin && t > fin) return false;
    return true;
  };
  const ventes = debut || fin ? allValidated.filter(inPeriod) : allValidated;

  const libre = ventes.filter((v) => v.type_vente === "LIBRE");
  const chifa = ventes.filter((v) => v.type_vente === "CHIFA");

  const ca_total = money(ventes.reduce((s, v) => s + (v.montant_total_ttc || 0), 0));
  const ca_libre = money(libre.reduce((s, v) => s + (v.montant_total_ttc || 0), 0));
  const ca_chifa = money(chifa.reduce((s, v) => s + (v.montant_total_ttc || 0), 0));
  const pec = money(chifa.reduce((s, v) => s + (v.montant_pec_chifa || 0), 0));
  const patient = money(chifa.reduce((s, v) => s + (v.montant_a_payer || 0), 0));
  const clientIds = new Set(ventes.map((v) => v.client_id).filter(Boolean));

  // Gross margin estimate from sold revenue vs cost (COST_RATIO of PPA).
  const cost = money(ca_total * COST_RATIO);
  const marge_pct = ca_total > 0 ? +(((ca_total - cost) / ca_total) * 100).toFixed(1) : 0;

  const produitsActifs = store.data.produits.filter((p) => p.statut_produit !== "BLOQUE_CACHE");
  const rupture = produitsActifs.filter((p) => (p.stock_actuel || 0) === 0).length;
  const lowStock = produitsActifs.filter(
    (p) => (p.stock_actuel || 0) > 0 && (p.stock_actuel || 0) < (p.seuil_alerte || 0),
  ).length;

  // Lots expiring within 90 days (peremption alert bucket).
  const horizon = ymdDaysAgo(-90); // 90 days in the FUTURE
  const todayYmd = new Date().toISOString().slice(0, 10);
  const peremptionProduits = new Set(
    (store.data.stockLots || [])
      .filter(
        (l) =>
          (l.quantite_actuelle || 0) > 0 &&
          l.date_peremption &&
          l.date_peremption >= todayYmd &&
          l.date_peremption <= horizon,
      )
      .map((l) => l.produit_id),
  );
  const peremption = peremptionProduits.size;

  const valeur_stock = money(
    store.data.stockLots.reduce((s, l) => {
      const p = store.data.produits.find((x) => x.id === l.produit_id);
      return s + (l.quantite_actuelle || 0) * (p?.ppa || 0);
    }, 0),
  );

  // ── Today vs yesterday (headline "CA jour" card) ───────────────────────────
  const today = todayYmd;
  const yesterday = ymdDaysAgo(1);
  const todayVentes = allValidated.filter((v) => (v.date_vente || "").slice(0, 10) === today);
  const ca_jour = money(caForDay(allValidated, today));
  const ca_hier = money(caForDay(allValidated, yesterday));
  const ca_jour_delta = ca_hier > 0 ? deltaPct(ca_jour, ca_hier) : FALLBACK_DELTA.ca_jour;
  const todayClients = new Set(todayVentes.map((v) => v.client_id).filter(Boolean));

  // ── This month vs last month (headline "CA mois" + panier moyen) ───────────
  const now = new Date();
  const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const thisMonth = monthKey(now);
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonth = monthKey(lastMonthDate);
  const inMonth = (v, key) => (v.date_vente || "").slice(0, 7) === key;

  const moisVentes = allValidated.filter((v) => inMonth(v, thisMonth));
  const moisPrevVentes = allValidated.filter((v) => inMonth(v, lastMonth));
  const ca_mois = money(moisVentes.reduce((s, v) => s + (v.montant_total_ttc || 0), 0));
  const ca_mois_prev = money(moisPrevVentes.reduce((s, v) => s + (v.montant_total_ttc || 0), 0));
  const ca_mois_delta = ca_mois_prev > 0 ? deltaPct(ca_mois, ca_mois_prev) : FALLBACK_DELTA.ca_mois;

  const panier = moisVentes.length ? money(ca_mois / moisVentes.length) : (ventes.length ? money(ca_total / ventes.length) : 0);
  const panier_prev = moisPrevVentes.length ? money(ca_mois_prev / moisPrevVentes.length) : 0;
  const panier_delta = panier_prev > 0 ? deltaPct(panier, panier_prev) : FALLBACK_DELTA.panier_moyen;

  // EN_COURS sales = "file d'attente" / ventes à valider.
  const enCoursVentes = store.data.ventes.filter((v) => v.statut === "EN_COURS");
  const enCours = enCoursVentes.length;
  const enCoursMontant = money(enCoursVentes.reduce((s, v) => s + (v.montant_total_ttc || 0), 0));

  // ── Achats à valider (BROUILLON) — count + total + list for the manager ─────
  const brouillonAchats = (store.data.achats || []).filter((a) => a.statut === "BROUILLON");
  const achatsMontant = money(brouillonAchats.reduce((s, a) => s + (a.montant_ttc || 0), 0));
  const achats_a_valider_list = brouillonAchats
    .slice()
    .sort((a, b) => (a.date_facture < b.date_facture ? 1 : -1))
    .map((a) => ({
      id: a.id,
      numero_interne: a.numero_interne,
      fournisseur: a.fournisseur ? { nom: a.fournisseur.nom } : "—",
      nb_lignes: (a.details || []).length,
      montant_ttc: money(a.montant_ttc || 0),
      statut: a.statut,
      date_facture: a.date_facture,
    }));

  const fournisseursActifs = (store.data.fournisseurs || []).length;
  const precommandes = (store.data.efactures || []).filter(
    (e) => e.type === "PRECOMMANDE" || e.statut === "PRECOMMANDE_ENVOYEE",
  ).length;

  // ── charts: revenue trend (label/value), category split, top products ──────
  const trendMonths = Number(params.trend_mois) || 1;
  const trendDays = Math.min(90, Math.max(14, trendMonths * 30));
  const ca_par_jour = [];
  const todayD = new Date();
  for (let i = trendDays - 1; i >= 0; i -= 1) {
    const d = new Date(todayD);
    d.setDate(d.getDate() - i);
    const ymd = d.toISOString().slice(0, 10);
    const dayTotal = caForDay(allValidated, ymd);
    ca_par_jour.push({
      label: `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}`,
      value: money(dayTotal),
      date: ymd,
    });
  }

  // ca_trend: the role-based dashboard feeds this straight into <LineChart>
  // (data prop = array of NUMBERS) and slices the last 7 for the sparkline.
  // Last 30 days of daily validated revenue.
  const ca_trend = [];
  for (let i = 29; i >= 0; i -= 1) {
    ca_trend.push(money(caForDay(allValidated, ymdDaysAgo(i))));
  }

  // Repartition by sale type (LIBRE vs CHIFA vs INSTANCE) for the pie/mix chart.
  const instance = store.data.ventes.filter((v) => v.type_vente === "INSTANCE");
  const repartition = [
    { label: "Vente libre", value: ca_libre },
    { label: "Tiers payant CHIFA", value: ca_chifa },
    {
      label: "Instance à terme",
      value: money(instance.reduce((s, v) => s + (v.montant_total_ttc || 0), 0)),
    },
  ].filter((r) => r.value > 0);

  // stock_repartition: the Proprietaire <DonutChart> reads {label, value, color}
  // where `color` is a design-token suffix (success/warning/danger/accent →
  // var(--color-<x>)). Buckets the ACTIVE SKU count by stock health.
  const okStock = produitsActifs.filter(
    (p) => (p.stock_actuel || 0) > 0 && (p.stock_actuel || 0) >= (p.seuil_alerte || 0),
  ).length;
  const stock_repartition = [
    { label: "Stock sain", value: okStock, color: "success" },
    { label: "Stock bas", value: lowStock, color: "warning" },
    { label: "Rupture", value: rupture, color: "danger" },
    { label: "Péremption < 90j", value: peremption, color: "accent" },
  ].filter((r) => r.value > 0);

  const topRaw = topProduits(store, 8);
  // Flat shape for the analytics dashboard's chart block …
  const top = topRaw.map((t) => ({
    nom: t.nom_commercial,
    nom_commercial: t.nom_commercial,
    qte: t.quantite_vendue,
    quantite: t.quantite_vendue,
    ca: t.montant_ttc,
    montant: t.montant_ttc,
  }));
  // … and the SAME shape for the role-based ProprietaireDashboard, which maps
  // each item to {label: p.nom ?? p.nom_commercial, value: p.ca, sub: `${p.qte} u.`}.
  const top_products = top;

  const dernieres_ventes = [...allValidated]
    .sort((a, b) => (a.date_vente < b.date_vente ? 1 : -1))
    .slice(0, 8)
    .map((v) => ({
      numero: v.numero_interne,
      heure: (v.date_vente || "").slice(11, 16),
      montant: v.montant_total_ttc,
      type_vente: v.type_vente,
    }));

  // recent_ventes: the CaissierDashboard "Mes dernières ventes" table reads
  // {id, numero_interne, type_vente, date_vente, nb_lignes, montant_ttc, statut}.
  const recent_ventes = [...allValidated]
    .sort((a, b) => (a.date_vente < b.date_vente ? 1 : -1))
    .slice(0, 8)
    .map(venteRow);

  // recent_audit: the Proprietaire "Activité récente" timeline.
  const recent_audit = [...(store.data.audit || [])]
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .slice(0, 6)
    .map(auditEvent);

  const totalSku = produitsActifs.length;
  const nbUsers = (store.data.users || []).length;

  // ───────────────────────────────────────────────────────────────────────────
  // REAL /dashboard payload shape (camelCase) — mirrors the server's
  // getDashboardData so the design-system role dashboards
  // (client/src/pages/dashboards/Dashboards.jsx), which read the REAL shape,
  // render in BETA mode too. Everything below is ADDITIVE: the snake_case fields
  // in the return are kept for the analytics dashboard, /dashboard/compare and
  // other legacy readers.
  // ───────────────────────────────────────────────────────────────────────────

  const annulees = store.data.ventes.filter((v) => v.statut === "ANNULEE");
  const validAchats = (store.data.achats || []).filter((a) => a.statut === "VALIDE");
  const totalAchatTTC = money(validAchats.reduce((s, a) => s + (a.montant_ttc || 0), 0));
  const totalAchatHT = money(
    validAchats.reduce((s, a) => s + (a.montant_ht ?? (a.montant_ttc || 0) / 1.09), 0),
  );

  // Stock lot health buckets (real stock.lots*).
  const allLots = store.data.stockLots || [];
  const lotHasStock = (l) => (l.quantite_actuelle || 0) > 0;
  const in30Ymd = ymdDaysAgo(-30);
  const lotsActifs = allLots.filter(lotHasStock).length;
  const lotsPerimes = allLots.filter(
    (l) => lotHasStock(l) && l.date_peremption && l.date_peremption < todayYmd,
  ).length;
  const lotsExpirant30j = allLots.filter(
    (l) => lotHasStock(l) && l.date_peremption && l.date_peremption >= todayYmd && l.date_peremption <= in30Ymd,
  ).length;
  const lotsProchesPeremption = allLots.filter(
    (l) => lotHasStock(l) && l.date_peremption && l.date_peremption >= todayYmd && l.date_peremption <= horizon,
  ).length;

  // charts.trend — monthly CA evolution (real { mois, caTotal, caChifa, caLibre, achats }).
  const trendMonthsCount = Number(params.trend_mois) || 6;
  const trendMonthKeys = [];
  {
    const cur = new Date(now.getFullYear(), now.getMonth() - (trendMonthsCount - 1), 1);
    while (cur <= now) {
      trendMonthKeys.push(
        `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`,
      );
      cur.setMonth(cur.getMonth() + 1);
    }
  }
  const trendReal = trendMonthKeys.map((mois) => {
    const mv = allValidated.filter((v) => (v.date_vente || "").slice(0, 7) === mois);
    const ct = mv.reduce((s, v) => s + (v.montant_total_ttc || 0), 0);
    const cc = mv
      .filter((v) => v.type_vente === "CHIFA")
      .reduce((s, v) => s + (v.montant_total_ttc || 0), 0);
    const ach = validAchats
      .filter((a) => (a.date_facture || "").slice(0, 7) === mois)
      .reduce((s, a) => s + (a.montant_ttc || 0), 0);
    return {
      mois,
      caTotal: money(ct),
      caChifa: money(cc),
      caLibre: money(ct - cc),
      achats: money(ach),
    };
  });

  // charts.distribution — vente type breakdown (real { type_vente, count, total }).
  const distMap = {};
  for (const v of ventes) {
    const t = v.type_vente || "LIBRE";
    if (!distMap[t]) distMap[t] = { type_vente: t, count: 0, total: 0 };
    distMap[t].count += 1;
    distMap[t].total += v.montant_total_ttc || 0;
  }
  const distributionReal = Object.values(distMap).map((d) => ({
    type_vente: d.type_vente,
    count: d.count,
    total: money(d.total),
  }));

  // charts.topProduits — real { nom_commercial, qteVendue, caTotal, typeProduit }.
  const topProduitsReal = topProduits(store, 10).map((t) => {
    const p = store.data.produits.find((x) => x.id === t.produit_id);
    return {
      nom_commercial: t.nom_commercial,
      qteVendue: t.quantite_vendue,
      caTotal: t.montant_ttc,
      typeProduit: p?.type_produit ?? null,
    };
  });

  // charts.produitsLents — real { produit_id, nom, type, qte, valeur_pa, derniere_vente }.
  const soldMap = soldQtyByProduct(store);
  const produitsLentsReal = produitsActifs
    .filter((p) => (p.stock_actuel || 0) > 0 && !(soldMap.get(p.id) > 0))
    .map((p) => {
      const jours = 60 + (p.id.charCodeAt(p.id.length - 1) % 120);
      return {
        produit_id: p.id,
        nom: p.nom_commercial,
        type: p.type_produit,
        qte: p.stock_actuel || 0,
        valeur_pa: money((p.stock_actuel || 0) * (p.ppa || 0) * COST_RATIO),
        derniere_vente: dateDaysAgo(jours),
      };
    })
    .sort((a, b) => b.valeur_pa - a.valeur_pa)
    .slice(0, 8);

  // charts.dailySales — real { jour, caTotal, nbVentes } (last 30 days).
  const dailySalesReal = [];
  for (let i = 29; i >= 0; i -= 1) {
    const ymd = ymdDaysAgo(i);
    const dv = allValidated.filter((v) => (v.date_vente || "").slice(0, 10) === ymd);
    dailySalesReal.push({
      jour: ymd,
      caTotal: money(dv.reduce((s, v) => s + (v.montant_total_ttc || 0), 0)),
      nbVentes: dv.length,
    });
  }

  // eFactures — real { total, enAttente, validees, rejetees, montantValide }.
  const efs = store.data.efactures || [];
  const efAttente = efs.filter((e) =>
    ["ENVOYEE", "NOTIFIEE", "IMPORTEE", "RECUE"].includes(e.statut),
  );
  const efValidees = efs.filter((e) =>
    ["VALIDEE", "STOCK_VALIDE", "PAYEE"].includes(e.statut),
  );
  const efRejetees = efs.filter((e) => e.statut === "REJETEE");

  return {
    kpis: {
      // ── ProprietaireDashboard: NESTED headline KPIs ────────────────────────
      ca_jour: { value: ca_jour, delta_pct: ca_jour_delta },
      ca_mois: { value: ca_mois, delta_pct: ca_mois_delta },
      panier_moyen: { value: panier, delta_pct: panier_delta },
      marge_brute: { value: marge_pct, delta_pct: FALLBACK_DELTA.marge_brute },
      alertes_stock: {
        value: rupture + lowStock + peremption,
        breakdown: { rupture, bas: lowStock, peremption },
      },
      ca_trend,
      stock_repartition,
      total_sku: totalSku,
      top_products,
      recent_audit,
      recent_ventes,
      nb_users: nbUsers,

      // ── ManagerDashboard: NESTED operational KPIs ──────────────────────────
      achats_a_valider: { value: brouillonAchats.length, montant: achatsMontant },
      achats_a_valider_list,
      ventes_a_valider: { value: enCours, montant: enCoursMontant },
      stock_critique: { value: rupture + lowStock },
      fournisseurs_actifs: { value: fournisseursActifs },
      pre_commandes_en_attente: { value: precommandes },

      // ── FLAT legacy headline fields (analytics dashboard, /compare, …) ─────
      ca_total,
      ca_libre,
      ca_chifa,
      nb_ventes: ventes.length,
      marge_pct,
      taux_marge: marge_pct,
      nb_clients: clientIds.size,
      nb_produits: produitsActifs.length,
      nb_ordonnances: chifa.length,
      file_attente: enCours,
      // caissier "today" (flat)
      nb_ventes_jour: todayVentes.length,
      nb_clients_jour: todayClients.size,
      // legacy aliases (kept so any other reader still works)
      ca_total_ttc: ca_total,
      nb_clients_uniques: clientIds.size,

      // ── REAL shape (camelCase) — mirrors server getDashboardData.kpis ───────
      caTotal: ca_total,
      caChifa: ca_chifa,
      caLibre: ca_libre,
      nbVentes: ventes.length,
      nbChifa: chifa.length,
      ticketMoyen: panier,
      nbAnnulations: annulees.length,
      montantAnnule: money(annulees.reduce((s, v) => s + (v.montant_total_ttc || 0), 0)),
      remiseTotale: 0,
      margeBrute: money(ca_total - cost),
      tauxMarge: marge_pct,
      totalAchatHT,
      totalAchatTTC,
      nbAchats: validAchats.length,
      nbFournisseursActifs: fournisseursActifs,
      valeurStockPPA: valeur_stock,
      produitsEnRupture: rupture,
    },
    comparaison: {
      ca_total: { current: ca_total, previous: money(ca_total * 0.86), variation_pct: 16.3 },
      nb_ventes: { current: ventes.length, previous: Math.floor(ventes.length * 0.86), variation_pct: 16.3 },
      // REAL shape — numeric period-over-period % deltas (server comparaison.*)
      caTotal: 16.3,
      caChifa: 9.8,
      nbVentes: 16.3,
      ticketMoyen: panier_delta,
      margeBrute: FALLBACK_DELTA.marge_brute,
    },
    stock: {
      valeur_stock,
      nb_references: produitsActifs.length,
      nb_alertes: rupture + lowStock,
      nb_ruptures: rupture,
      // legacy aliases
      valeur_stock_ttc: valeur_stock,
      nb_lots_actifs: store.data.stockLots.length,
      // REAL shape — server stock.* (getStockKPI + getStockAlertCounts)
      produitsEnRupture: rupture,
      produitsStockBas: lowStock,
      lotsActifs,
      lotsPerimes,
      lotsExpirant30j,
      lotsProchesPeremption,
      valeurStockPPA: valeur_stock,
      valeurStockPA: money(valeur_stock * COST_RATIO),
    },
    chifa: {
      nb_ordonnances: chifa.length,
      taux_remboursement: ca_chifa > 0 ? +((pec / ca_chifa) * 100).toFixed(1) : 80,
      montant_rembourse: pec,
      ca_chifa,
      montant_attente: patient,
      // legacy aliases
      nb_ventes_chifa: chifa.length,
      montant_pec_total: pec,
      montant_patient_total: patient,
      // REAL shape — server chifa.* (getChifaKPI)
      partOfficineTotale: pec,
      partAssureTotale: patient,
      majorationTotale: 0,
      nbVentesChifa: chifa.length,
    },
    charts: {
      ca_par_jour,
      repartition,
      top_produits: top,
      dernieres_ventes,
      // REAL shape — server charts.* (getCATrendByMonth, getVenteTypeDistribution,
      // getTopProduits, getStockMort, getDailySales)
      trend: trendReal,
      distribution: distributionReal,
      topProduits: topProduitsReal,
      produitsLents: produitsLentsReal,
      dailySales: dailySalesReal,
    },
    eFactures: {
      count_recue: store.data.efactures.filter((e) => e.statut === "RECUE").length,
      count_stock_valide: store.data.efactures.filter((e) => e.statut === "STOCK_VALIDE").length,
      count_payee: store.data.efactures.filter((e) => e.statut === "PAYEE").length,
      // REAL shape — server eFactures (getEFacturesKPI)
      total: efs.length,
      enAttente: efAttente.length,
      validees: efValidees.length,
      rejetees: efRejetees.length,
      montantValide: money(
        efValidees.reduce((s, e) => s + (e.montant_ttc || e.montant_total_ttc || 0), 0),
      ),
    },
    meta: { generated_at: nowIso(), debut, fin: params.fin || null },
  };
}
